// biome-ignore lint/style/noRestrictedImports: component usage
import { Root, type ToggleGroupSingleProps } from "@radix-ui/react-toggle-group";
import { useComputed } from "@suse-o11y/signal";
import React from "react";

import { useFormFieldContext } from "~/components/form/use-form-field-context";
import { cx } from "~/tools/class-variance";
import { useOnInvalidFormFocus } from "~/tools/impulse-form/use-on-invalid-form-focus";
import { mergeRefs } from "~/tools/merge-refs";

/**
 * Check out Figma for the design of the radio button group:
 *
 * @link https://www.figma.com/design/YIkZms7ygvgqXYnbzDFr8Y/--StackState-DS?node-id=4370-1309&m=dev
 */

interface RadioButtonGroupProps extends Omit<ToggleGroupSingleProps, "type"> {
	canHaveEmptyValue?: boolean;
}

export const RadioButtonGroup = React.forwardRef<React.ElementRef<typeof Root>, RadioButtonGroupProps>(
	({ className, children, onValueChange, canHaveEmptyValue = false, onBlur, ...props }, ref) => {
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus(formField?.state);
		const submitting = useComputed((monitor) => formField?.state.isSubmitting(monitor), [formField?.state]);

		return (
			<Root
				aria-busy={submitting}
				id={formField?.id}
				ref={mergeRefs([ref, focusRef])}
				type="single"
				{...props}
				className={cx("radio-button-group", className)}
				onBlur={(event) => {
					formField?.state.setTouched(true);
					onBlur?.(event);
				}}
				onValueChange={(value) => {
					if (canHaveEmptyValue || value !== "") {
						onValueChange?.(value);
					}
				}}
			>
				{children}
			</Root>
		);
	},
);

if (import.meta.env.DEV) {
	RadioButtonGroup.displayName = "RadioButtonGroup";
}
